import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCourseCreation } from '../../contexts/CourseCreationContext';
import VideoEditor from './editors/VideoEditor';
import InteractiveEditor from './editors/InteractiveEditor';
import ExerciseEditor from './editors/ExerciseEditor';
import DocumentEditor from './editors/DocumentEditor';
import QuizEditor from './editors/QuizEditor';
import AssignmentEditor from './editors/AssignmentEditor';
import { CheckCircle, AlertCircle, ArrowLeft, Plus, Layers, Trash2 } from 'lucide-react';

const SimpleContentEditor = () => {
  const { courseId } = useParams();
  const navigate     = useNavigate();
  const { contentTypes, createContentDraft, saveContentDraft, createModule, getContentType } = useCourseCreation();

  // ── État modules ──────────────────────────────────────────────────────────
  const [modules,        setModules]        = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [loadingModules, setLoadingModules] = useState(true);
  const [moduleError,    setModuleError]    = useState(null);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [creatingModule, setCreatingModule] = useState(false);

  // ── État éditeur de contenu ───────────────────────────────────────────────
  const [selectedType,   setSelectedType]   = useState(null);
  const [createdContent, setCreatedContent] = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [saveError,      setSaveError]      = useState(null);
  const [saveSuccess,    setSaveSuccess]    = useState(false);

  // ── État formulaire évaluation ────────────────────────────────────────────
  const [evalForm, setEvalForm] = useState({
    title: '',
    description: '',
    instructions: '',
    passingScore: 70,
    timeLimit: '',
    maxAttempts: 3,
    questions: [{ id: 1, type: 'multiple-choice', text: '', points: 1, options: ['', '', '', ''], correct: 0 }],
  });

  // ── Chargement des modules du cours ──────────────────────────────────────
  useEffect(() => {
    if (!courseId) return;
    const fetchModules = async () => {
      setLoadingModules(true);
      setModuleError(null);
      try {
        const { data } = await axios.get(`/api/modules/course/${courseId}`);
        const mods = data.modules || [];
        setModules(mods);
        if (mods.length === 1) setSelectedModule(mods[0]); // sélection auto si 1 seul module
      } catch (err) {
        setModuleError(err.response?.data?.error || err.message);
      } finally {
        setLoadingModules(false);
      }
    };
    fetchModules();
  }, [courseId]);

  // ── Création d'un module ──────────────────────────────────────────────────
  const handleCreateModule = async () => {
    const title = newModuleTitle.trim() || 'Module 1';
    setCreatingModule(true);
    setModuleError(null);
    const result = await createModule({ courseId, title, order: modules.length });
    if (result.success) {
      const mod = result.module;
      setModules(prev => [...prev, mod]);
      setSelectedModule(mod);
      setNewModuleTitle('');
    } else {
      setModuleError(result.error || 'Erreur lors de la création du module');
    }
    setCreatingModule(false);
  };

  // ── Sélection du type de contenu ─────────────────────────────────────────
  const handleTypeSelect = (typeId) => {
    const type = getContentType(typeId);
    if (type && selectedModule) {
      setSelectedType(type);
      const draft = createContentDraft(courseId, selectedModule.id, typeId);
      setCreatedContent(draft);
      setSaveError(null);
      setSaveSuccess(false);
    }
  };

  // ── Mise à jour du contenu par l'éditeur ─────────────────────────────────
  const handleContentUpdate = (updatedContent) => {
    setCreatedContent(updatedContent);
  };

  // ── Sauvegarde ───────────────────────────────────────────────────────────
  const handleSave = async (content) => {
    if (!createdContent) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const result = await saveContentDraft(createdContent.id, content);

    if (result.success) {
      setCreatedContent(prev => ({ ...prev, lessonId: result.lesson?.id || prev.lessonId, status: 'saved' }));
      setSaveSuccess(true);
      setTimeout(() => navigate('/instructor'), 1500);
    } else {
      setSaveError(result.error || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const handlePreview = (content) => {
    setCreatedContent(content);
  };

  // ── Ajout/suppression de questions évaluation ────────────────────────────
  const addQuestion = () => {
    setEvalForm(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        { id: Date.now(), type: 'multiple-choice', text: '', points: 1, options: ['', '', '', ''], correct: 0 },
      ],
    }));
  };

  const removeQuestion = (qId) => {
    setEvalForm(prev => ({ ...prev, questions: prev.questions.filter(q => q.id !== qId) }));
  };

  const updateQuestion = (qId, field, value) => {
    setEvalForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => q.id === qId ? { ...q, [field]: value } : q),
    }));
  };

  const updateOption = (qId, optIdx, value) => {
    setEvalForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id !== qId) return q;
        const options = [...q.options];
        options[optIdx] = value;
        return { ...q, options };
      }),
    }));
  };

  const handleSaveEvaluation = async () => {
    if (!evalForm.title.trim()) { setSaveError('Le titre est requis'); return; }
    if (evalForm.questions.length === 0) { setSaveError('Au moins une question est requise'); return; }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const questions = evalForm.questions.map((q, idx) => ({
        id: idx + 1,
        type: q.type,
        text: q.text,
        points: Number(q.points) || 1,
        options: q.type === 'multiple-choice' ? q.options : ['Vrai', 'Faux'],
        correctAnswer: q.type === 'multiple-choice' ? q.correct : q.correct,
      }));
      await axios.post('/api/evaluations', {
        moduleId:     selectedModule.id,
        courseId,
        title:        evalForm.title,
        description:  evalForm.description,
        instructions: evalForm.instructions,
        passingScore: Number(evalForm.passingScore),
        timeLimit:    evalForm.timeLimit ? Number(evalForm.timeLimit) : null,
        maxAttempts:  Number(evalForm.maxAttempts),
        questions,
      });
      setSaveSuccess(true);
      setTimeout(() => navigate('/instructor'), 1500);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Rendu de l'éditeur spécialisé ────────────────────────────────────────
  const renderEditor = () => {
    if (!createdContent || !selectedType) return null;
    const props = {
      content:   createdContent,
      onUpdate:  handleContentUpdate,
      onSave:    handleSave,
      onPreview: handlePreview,
    };
    switch (selectedType.id) {
      case 'video':       return <VideoEditor       {...props} />;
      case 'interactive': return <InteractiveEditor {...props} />;
      case 'exercise':    return <ExerciseEditor    {...props} />;
      case 'document':    return <DocumentEditor    {...props} />;
      case 'quiz':        return <QuizEditor        {...props} />;
      case 'assignment':  return <AssignmentEditor  {...props} />;
      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">
              Éditeur pour « {selectedType.name} » en cours de développement…
            </p>
          </div>
        );
    }
  };

  // ── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/instructor')}
            className="p-1 text-gray-500 hover:text-gray-800 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Créateur de contenu de cours
          </h1>
        </div>

        {/* Feedback */}
        {saveError && (
          <div className="flex items-center gap-2 mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {saveError}
          </div>
        )}
        {saveSuccess && (
          <div className="flex items-center gap-2 mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-green-700 text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            Contenu sauvegardé avec succès ! Redirection…
          </div>
        )}
        {saving && (
          <div className="flex items-center gap-2 mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-blue-700 text-sm">
            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            Sauvegarde en cours…
          </div>
        )}

        {/* ── Étape 1 : Sélection / création du module ── */}
        {!selectedModule ? (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5" />
              {loadingModules ? 'Chargement des modules…' : 'Choisissez ou créez un module'}
            </h2>

            {moduleError && (
              <div className="flex items-center gap-2 mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {moduleError}
              </div>
            )}

            {loadingModules ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              <>
                {/* Modules existants */}
                {modules.length > 0 && (
                  <div className="space-y-2 mb-6">
                    {modules.map(mod => (
                      <button
                        key={mod.id}
                        onClick={() => setSelectedModule(mod)}
                        className="w-full text-left px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary transition-colors"
                      >
                        <span className="font-medium text-gray-900 dark:text-white">{mod.title}</span>
                        {mod.lessons?.length > 0 && (
                          <span className="ml-2 text-sm text-gray-500">({mod.lessons.length} leçon{mod.lessons.length > 1 ? 's' : ''})</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Création d'un nouveau module */}
                <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {modules.length === 0 ? 'Aucun module — créez-en un pour commencer :' : 'Ou créez un nouveau module :'}
                  </p>
                  <form
                    onSubmit={e => { e.preventDefault(); handleCreateModule(); }}
                    className="flex gap-2"
                  >
                    <input
                      type="text"
                      value={newModuleTitle}
                      onChange={e => setNewModuleTitle(e.target.value)}
                      placeholder="Ex: Introduction, Module 1…"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <button
                      type="submit"
                      disabled={creatingModule}
                      className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm"
                    >
                      {creatingModule
                        ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <Plus className="w-4 h-4" />}
                      Créer
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>

        /* ── Étape 2 : Sélection du type de contenu ── */
        ) : !selectedType ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Module : <span className="text-primary">{selectedModule.title}</span>
              </h2>
              <button
                onClick={() => setSelectedModule(null)}
                className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-white"
              >
                ← Changer de module
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Choisissez le type de contenu :</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contentTypes?.map((type) => {
                const TypeIcon = type.Icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary transition-colors text-left"
                  >
                    {TypeIcon && <TypeIcon className="w-6 h-6 mb-2 text-primary" />}
                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">{type.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{type.description}</p>
                  </button>
                );
              })}
              <button
                onClick={() => setSelectedType({ id: 'evaluation', name: 'Évaluation' })}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary transition-colors text-left"
              >
                <svg className="w-6 h-6 mb-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <h3 className="font-medium text-gray-900 dark:text-white mb-1">Évaluation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">QCM et vrai/faux avec score de passage</p>
              </button>
            </div>
          </div>

        /* ── Étape 3 : Éditeur de contenu ── */
        ) : selectedType.id === 'evaluation' ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Nouvelle évaluation — <span className="text-primary">{selectedModule.title}</span>
              </h2>
              <button
                onClick={() => { setSelectedType(null); setSaveError(null); setSaveSuccess(false); }}
                className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-white"
              >
                ← Changer de type
              </button>
            </div>
            <div className="space-y-4">
              {/* Informations générales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titre *</label>
                  <input
                    type="text"
                    value={evalForm.title}
                    onChange={e => setEvalForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Ex: Évaluation finale du module"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Score de passage (%)</label>
                  <input
                    type="number" min="0" max="100"
                    value={evalForm.passingScore}
                    onChange={e => setEvalForm(p => ({ ...p, passingScore: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tentatives max</label>
                  <input
                    type="number" min="1"
                    value={evalForm.maxAttempts}
                    onChange={e => setEvalForm(p => ({ ...p, maxAttempts: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Durée limite (min, optionnel)</label>
                  <input
                    type="number" min="1"
                    value={evalForm.timeLimit}
                    onChange={e => setEvalForm(p => ({ ...p, timeLimit: e.target.value }))}
                    placeholder="Ex: 30"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions (optionnel)</label>
                <textarea
                  value={evalForm.instructions}
                  onChange={e => setEvalForm(p => ({ ...p, instructions: e.target.value }))}
                  rows={2}
                  placeholder="Consignes pour les étudiants…"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Questions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900 dark:text-white">Questions</h3>
                  <button
                    onClick={addQuestion}
                    className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                  >
                    <Plus className="w-4 h-4" /> Ajouter une question
                  </button>
                </div>
                <div className="space-y-4">
                  {evalForm.questions.map((q, idx) => (
                    <div key={q.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <span className="text-sm font-medium text-gray-500">Q{idx + 1}</span>
                        <button
                          onClick={() => removeQuestion(q.id)}
                          className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div className="md:col-span-2">
                          <input
                            type="text"
                            value={q.text}
                            onChange={e => updateQuestion(q.id, 'text', e.target.value)}
                            placeholder="Texte de la question"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={q.type}
                            onChange={e => updateQuestion(q.id, 'type', e.target.value)}
                            className="flex-1 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                          >
                            <option value="multiple-choice">QCM</option>
                            <option value="true_false">Vrai/Faux</option>
                          </select>
                          <input
                            type="number" min="1"
                            value={q.points}
                            onChange={e => updateQuestion(q.id, 'points', Number(e.target.value))}
                            title="Points"
                            className="w-16 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                      </div>
                      {q.type === 'multiple-choice' ? (
                        <div className="space-y-2">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`correct-${q.id}`}
                                checked={q.correct === oi}
                                onChange={() => updateQuestion(q.id, 'correct', oi)}
                                className="text-primary"
                              />
                              <input
                                type="text"
                                value={opt}
                                onChange={e => updateOption(q.id, oi, e.target.value)}
                                placeholder={`Option ${oi + 1}`}
                                className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                              />
                            </div>
                          ))}
                          <p className="text-xs text-gray-400">Sélectionnez la bonne réponse avec le bouton radio</p>
                        </div>
                      ) : (
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name={`tf-${q.id}`} checked={q.correct === 0} onChange={() => updateQuestion(q.id, 'correct', 0)} />
                            Vrai
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="radio" name={`tf-${q.id}`} checked={q.correct === 1} onChange={() => updateQuestion(q.id, 'correct', 1)} />
                            Faux
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSaveEvaluation}
                disabled={saving}
                className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 font-medium"
              >
                {saving ? 'Sauvegarde…' : 'Créer l\'évaluation'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Éditeur : {selectedType.name}
              </h2>
              <button
                onClick={() => { setSelectedType(null); setCreatedContent(null); setSaveError(null); setSaveSuccess(false); }}
                className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-white"
              >
                ← Changer de type
              </button>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              {renderEditor()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleContentEditor;
